import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, CheckCircle, Users, ChevronLeft, AlertTriangle } from 'lucide-react';
import { authApi } from '../../api/client';
import { joinRequestsApi } from '../../api/client';
import { useAuthStore } from '../../store/useAuthStore';
import { Spinner } from '../../components/common/Spinner';

type Flow = 'choose' | 'create' | 'join' | 'join-pending';

export function OnboardingPage() {
  const navigate = useNavigate();
  const { user, setOrgContext } = useAuthStore();

  const [flow, setFlow] = useState<Flow>('choose');

  // ── Create flow ───────────────────────────────
  const [orgName, setOrgName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createDone, setCreateDone] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.email) {
      const local = user.email.split('@')[0];
      const capitalized = local.length > 0
        ? `${local.charAt(0).toUpperCase()}${local.slice(1)}`
        : 'My';
      setOrgName(`${capitalized}'s Workspace`);
    }
  }, [user?.email]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(null);
    setCreateLoading(true);
    try {
      const result = await authApi.onboard(orgName.trim() || undefined);
      setOrgContext(result);
      setCreateDone(true);
      setTimeout(() => navigate('/baseball/mlb/command-center', { replace: true }), 1200);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to set up workspace. Please try again.');
    } finally {
      setCreateLoading(false);
    }
  }

  // ── Join flow ─────────────────────────────────
  const [slug, setSlug] = useState('');
  const [joinMessage, setJoinMessage] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinedOrgName, setJoinedOrgName] = useState('');

  async function handleJoinRequest(e: React.FormEvent) {
    e.preventDefault();
    setJoinError(null);
    setJoinLoading(true);
    try {
      const result = await joinRequestsApi.requestToJoin(slug.trim(), joinMessage.trim() || undefined);
      setJoinedOrgName(result.organizationName);
      setFlow('join-pending');
    } catch (err) {
      setJoinError(err instanceof Error ? err.message : 'Failed to submit join request.');
    } finally {
      setJoinLoading(false);
    }
  }

  // ── Create success ────────────────────────────
  if (createDone) {
    return (
      <div className="min-h-screen bg-surface-0 flex flex-col items-center justify-center gap-4">
        <CheckCircle className="w-14 h-14 text-win" />
        <h2 className="text-xl font-semibold text-gray-100">Workspace ready!</h2>
        <p className="text-sm text-gray-500">Taking you in…</p>
      </div>
    );
  }

  // ── Join pending ──────────────────────────────
  if (flow === 'join-pending') {
    return (
      <div className="min-h-screen bg-surface-0 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md bg-surface-1 border border-surface-4 rounded-xl p-8 shadow-2xl text-center space-y-4">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-win/10 border border-win/25 mx-auto">
            <CheckCircle className="w-6 h-6 text-win" />
          </div>
          <h2 className="text-xl font-semibold text-gray-100">Request sent!</h2>
          <p className="text-sm text-gray-400">
            Your request to join <span className="text-gray-200 font-medium">{joinedOrgName}</span> has been
            submitted. The organization's owner or admins will review it and you'll gain access once accepted.
          </p>
          <p className="text-xs text-gray-600">
            You can close this page and check back later — you'll be able to sign in once your request is approved.
          </p>
        </div>
      </div>
    );
  }

  // ── Choice screen ─────────────────────────────
  if (flow === 'choose') {
    return (
      <div className="min-h-screen bg-surface-0 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md bg-surface-1 border border-surface-4 rounded-xl p-8 shadow-2xl">
          <h2 className="text-xl font-semibold text-gray-100 text-center mb-1">
            Welcome to Navyrx Sports
          </h2>
          <p className="text-sm text-gray-500 text-center mb-8">
            Get started by creating your own workspace or joining an existing team.
          </p>

          <div className="space-y-3">
            <button
              onClick={() => setFlow('create')}
              className="w-full flex items-start gap-4 px-5 py-4 bg-surface-2 border border-surface-4 rounded-lg hover:border-brand/50 hover:bg-surface-3 transition-colors text-left group"
            >
              <div className="p-2 bg-brand/10 border border-brand/20 rounded-lg flex-shrink-0 mt-0.5 group-hover:bg-brand/20 transition-colors">
                <Building2 className="w-5 h-5 text-brand" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-100">Create a new organization</p>
                <p className="text-xs text-gray-500 mt-0.5">Set up your own workspace and invite your team</p>
              </div>
            </button>

            <button
              onClick={() => setFlow('join')}
              className="w-full flex items-start gap-4 px-5 py-4 bg-surface-2 border border-surface-4 rounded-lg hover:border-brand/50 hover:bg-surface-3 transition-colors text-left group"
            >
              <div className="p-2 bg-brand/10 border border-brand/20 rounded-lg flex-shrink-0 mt-0.5 group-hover:bg-brand/20 transition-colors">
                <Users className="w-5 h-5 text-brand" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-100">Request to join an existing org</p>
                <p className="text-xs text-gray-500 mt-0.5">Find your team's workspace and request access</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Create org form ───────────────────────────
  if (flow === 'create') {
    return (
      <div className="min-h-screen bg-surface-0 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md bg-surface-1 border border-surface-4 rounded-xl p-8 shadow-2xl">
          <button
            onClick={() => setFlow('choose')}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 mb-5 transition-colors"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>

          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-brand/10 border border-brand/25 mx-auto mb-4">
            <Building2 className="w-6 h-6 text-brand" />
          </div>

          <h2 className="text-xl font-semibold text-gray-100 text-center mb-1">
            Set up your workspace
          </h2>
          <p className="text-sm text-gray-500 text-center mb-6">
            Give your organization a name to get started.
          </p>

          {createError && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-loss/10 border border-loss/25 text-sm text-loss flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {createError}
            </div>
          )}

          <form onSubmit={(e) => void handleCreate(e)} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5" htmlFor="orgName">
                Organization name
              </label>
              <input
                id="orgName"
                type="text"
                required
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className="w-full px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                placeholder="My Workspace"
              />
            </div>

            <button
              type="submit"
              disabled={createLoading}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createLoading ? <Spinner size="sm" /> : <Building2 className="w-4 h-4" />}
              {createLoading ? 'Setting up…' : 'Create workspace'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Join org form ─────────────────────────────
  return (
    <div className="min-h-screen bg-surface-0 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md bg-surface-1 border border-surface-4 rounded-xl p-8 shadow-2xl">
        <button
          onClick={() => setFlow('choose')}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 mb-5 transition-colors"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </button>

        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-brand/10 border border-brand/25 mx-auto mb-4">
          <Users className="w-6 h-6 text-brand" />
        </div>

        <h2 className="text-xl font-semibold text-gray-100 text-center mb-1">
          Request to join
        </h2>
        <p className="text-sm text-gray-500 text-center mb-6">
          Enter your organization's slug (the short ID in the URL) to request access.
        </p>

        {joinError && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-loss/10 border border-loss/25 text-sm text-loss flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            {joinError}
          </div>
        )}

        <form onSubmit={(e) => void handleJoinRequest(e)} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5" htmlFor="slug">
              Organization slug
            </label>
            <input
              id="slug"
              type="text"
              required
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
              placeholder="my-team"
            />
            <p className="mt-1 text-[11px] text-gray-600">
              Ask your team admin for the organization slug.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5" htmlFor="joinMessage">
              Message <span className="text-gray-600">(optional)</span>
            </label>
            <textarea
              id="joinMessage"
              value={joinMessage}
              onChange={(e) => setJoinMessage(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand resize-none"
              placeholder="Briefly introduce yourself to the org admins…"
            />
          </div>

          <button
            type="submit"
            disabled={joinLoading || !slug.trim()}
            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {joinLoading ? <Spinner size="sm" /> : <Users className="w-4 h-4" />}
            {joinLoading ? 'Sending request…' : 'Send join request'}
          </button>
        </form>
      </div>
    </div>
  );
}
